import { buildPlatformScope, type Question } from '@examflow/core';
import type {
  CreateTrainingAttemptRecordData,
  TrainingAttemptQuestionMetric,
  TrainingAttemptSectionSummary,
} from '../../lib/api';
import type { QuizTrainingAttemptContext } from './quizTrainingContext';
import { buildTrainingAttemptEvidenceCopy } from './trainingAttemptEvidence';
import {
  normalizeWritingAccuracyFromScore,
  resolveOpenEndedMeta,
  resolveQuestionTopicIds,
} from './openEndedWriting';

type SectionedQuestion = {
  id: string;
  section?: string;
  level?: string | null;
  type?: string;
  taskNumber?: number;
  task?: number;
  topics?: string[];
  sourceType?: string | null;
  sourceTaskId?: string | null;
  sourceMetadata?: unknown;
};

export type SubmittedQuizAnswer = {
  questionId: string;
  answer: string;
  isCorrect: boolean;
  timeSpent: number;
  sectionId?: string;
  responseMode?: 'closed' | 'open_ended';
  wordCount?: number;
  aiScore?: number;
  /** Rubric ceiling for aiScore; omit on legacy answers → normalize as /20. */
  aiScoreMax?: number;
  /** Heuristic fallback grades must not drive Ability accuracy. */
  aiGradeSource?: 'llm' | 'heuristic';
  aiLevelHint?: string;
  aiFeedback?: string;
  aiDimensions?: Array<{
    id: string;
    label: string;
    score: number;
    action: string;
  }>;
};

/**
 * Build the training-records payload for a completed practice Attempt.
 * Thin adapter over POST /learning/training-records — no new server Attempt table.
 */
export function buildTrainingAttemptPersistPayload(input: {
  finalAnswers: SubmittedQuizAnswer[];
  questions: SectionedQuestion[];
  trainingAttemptContext: QuizTrainingAttemptContext;
  questionSourceByQuestionId?: Record<string, SectionedQuestion> | null;
}): CreateTrainingAttemptRecordData | null {
  const { finalAnswers, questions, trainingAttemptContext, questionSourceByQuestionId } = input;
  if (questions.length === 0) return null;

  const completedAt = Date.now();
  const openEndedAnswers = finalAnswers.filter((answer) => answer.responseMode === 'open_ended');
  const closedAnswers = finalAnswers.filter((answer) => answer.responseMode !== 'open_ended');
  const writingOnly = openEndedAnswers.length > 0 && closedAnswers.length === 0;
  const abilityGradeAnswers = openEndedAnswers.filter(
    (answer) => answer.aiGradeSource !== 'heuristic' && typeof answer.aiScore === 'number'
  );

  const correctCount = writingOnly
    ? 0
    : finalAnswers.filter((answer) => answer.isCorrect).length;
  const answeredCount = finalAnswers.length;
  const durationSeconds = Math.max(
    0,
    Math.round(finalAnswers.reduce((total, answer) => total + answer.timeSpent, 0) / 1000)
  );
  const accuracy = writingOnly
    ? abilityGradeAnswers.length > 0
      ? Math.round(
          abilityGradeAnswers.reduce(
            (sum, answer) =>
              sum + normalizeWritingAccuracyFromScore(answer.aiScore, answer.aiScoreMax),
            0
          ) / abilityGradeAnswers.length
        )
      : 0
    : answeredCount > 0
      ? Math.round((correctCount / answeredCount) * 100)
      : 0;
  const questionById = new Map(questions.map((question) => [question.id, question]));
  const getAnswerSectionId = (answer: SubmittedQuizAnswer): string =>
    answer.sectionId ||
    (questionById.get(answer.questionId)?.section ?? trainingAttemptContext.moduleId);

  const questionMetrics: TrainingAttemptQuestionMetric[] = finalAnswers.map((answer) => {
    const question = questionById.get(answer.questionId);
    const sourceTrace = questionSourceByQuestionId?.[answer.questionId] ?? question ?? null;
    const evidenceSource = (
      sourceTrace?.sourceMetadata as { evidenceSource?: string } | undefined
    )?.evidenceSource;
    const evidenceScope = (
      sourceTrace?.sourceMetadata as { evidenceScope?: string } | undefined
    )?.evidenceScope;

    const metric: TrainingAttemptQuestionMetric = {
      questionId: answer.questionId,
      sectionId: getAnswerSectionId(answer),
      level: question?.level ?? null,
      questionType: question?.type ?? null,
      sourceType: sourceTrace?.sourceType ?? null,
      sourceTaskId: sourceTrace?.sourceTaskId ?? null,
      sourceMetadata:
        (sourceTrace?.sourceMetadata as TrainingAttemptQuestionMetric['sourceMetadata']) ?? null,
      evidenceSource: evidenceSource || null,
      isCorrect: answer.responseMode === 'open_ended' ? false : answer.isCorrect,
      timeSpentSeconds: Math.max(0, Math.round(answer.timeSpent / 1000)),
    };
    if (evidenceScope === 'aggregate') {
      metric.evidenceScope = 'aggregate';
    }
    if (answer.responseMode === 'open_ended') {
      metric.responseMode = 'open_ended';
      metric.userAnswer = answer.answer;
      if (typeof answer.wordCount === 'number') metric.wordCount = answer.wordCount;
      if (typeof answer.aiScore === 'number') metric.aiScore = answer.aiScore;
      if (typeof answer.aiScoreMax === 'number') metric.aiScoreMax = answer.aiScoreMax;
      if (answer.aiGradeSource === 'heuristic' || answer.aiGradeSource === 'llm') {
        metric.aiGradeSource = answer.aiGradeSource;
      }
      if (answer.aiLevelHint) metric.aiLevelHint = answer.aiLevelHint;
      if (answer.aiFeedback) metric.aiFeedback = answer.aiFeedback;
      if (answer.aiDimensions?.length) metric.aiDimensions = answer.aiDimensions;
      if (question) {
        const taskNumber = resolveOpenEndedMeta(question as Question).taskNumber;
        if (taskNumber != null) metric.taskNumber = taskNumber;
        const topics = resolveQuestionTopicIds(question as Question);
        if (topics.length > 0) metric.topics = topics;
      }
    }
    return metric;
  });

  const totalQuestionsBySection = new Map<string, number>();
  questions.forEach((question) => {
    const section = question.section ?? trainingAttemptContext.moduleId;
    totalQuestionsBySection.set(section, (totalQuestionsBySection.get(section) ?? 0) + 1);
  });

  const sectionSummaryMap = new Map<
    string,
    Omit<TrainingAttemptSectionSummary, 'accuracy'> & {
      openEndedScores: Array<{ score: number; scoreMax?: number }>;
    }
  >();
  finalAnswers.forEach((answer) => {
    const section = getAnswerSectionId(answer);
    const previous =
      sectionSummaryMap.get(section) ??
      ({
        sectionId: section,
        sectionLabel:
          section === trainingAttemptContext.moduleId
            ? trainingAttemptContext.moduleLabel
            : section,
        totalQuestions: totalQuestionsBySection.get(section) ?? 0,
        answeredCount: 0,
        correctCount: 0,
        durationSeconds: 0,
        openEndedScores: [],
      } satisfies Omit<TrainingAttemptSectionSummary, 'accuracy'> & {
        openEndedScores: Array<{ score: number; scoreMax?: number }>;
      });

    const openEndedScores =
      answer.responseMode === 'open_ended' &&
      typeof answer.aiScore === 'number' &&
      answer.aiGradeSource !== 'heuristic'
        ? [
            ...previous.openEndedScores,
            { score: answer.aiScore, scoreMax: answer.aiScoreMax },
          ]
        : previous.openEndedScores;

    sectionSummaryMap.set(section, {
      ...previous,
      answeredCount: previous.answeredCount + 1,
      correctCount:
        answer.responseMode === 'open_ended'
          ? previous.correctCount
          : previous.correctCount + (answer.isCorrect ? 1 : 0),
      durationSeconds: previous.durationSeconds + Math.max(0, Math.round(answer.timeSpent / 1000)),
      openEndedScores,
    });
  });

  const sectionSummaries: TrainingAttemptSectionSummary[] = Array.from(
    sectionSummaryMap.values()
  ).map((summary) => {
    const { openEndedScores, ...rest } = summary;
    const accuracyFromWriting =
      openEndedScores.length > 0
        ? Math.round(
            openEndedScores.reduce(
              (sum, item) => sum + normalizeWritingAccuracyFromScore(item.score, item.scoreMax),
              0
            ) / openEndedScores.length
          )
        : null;
    return {
      ...rest,
      accuracy:
        accuracyFromWriting != null
          ? accuracyFromWriting
          : summary.answeredCount > 0
            ? Math.round((summary.correctCount / summary.answeredCount) * 100)
            : 0,
    };
  });

  const evidenceCopy = buildTrainingAttemptEvidenceCopy({
    moduleLabel: trainingAttemptContext.moduleLabel,
    intensityLabel: trainingAttemptContext.intensityLabel,
    source: trainingAttemptContext.source,
  });

  const platformScope = buildPlatformScope({
    examType: trainingAttemptContext.contentPack,
    skillId:
      trainingAttemptContext.source === 'diagnosis' ? null : trainingAttemptContext.moduleId,
  });

  const writingScoreLine =
    writingOnly && openEndedAnswers[0]?.aiScore != null
      ? `AI 评分 ${openEndedAnswers[0].aiScore}/${openEndedAnswers[0].aiScoreMax ?? 20}` +
        (openEndedAnswers[0].aiLevelHint ? `（${openEndedAnswers[0].aiLevelHint}）` : '')
      : null;

  return {
    sessionId: `quiz-${trainingAttemptContext.contentPack}-${trainingAttemptContext.moduleId}-${completedAt}`,
    contentPack: trainingAttemptContext.contentPack,
    moduleId: trainingAttemptContext.moduleId,
    moduleLabel: trainingAttemptContext.moduleLabel,
    intensity: trainingAttemptContext.intensity,
    intensityLabel: trainingAttemptContext.intensityLabel,
    totalQuestions: questions.length,
    answeredCount,
    correctCount,
    accuracy,
    durationSeconds,
    completedAt,
    resultTitle: writingOnly ? `${trainingAttemptContext.moduleLabel}写作评分` : evidenceCopy.resultTitle,
    resultSummary: writingScoreLine
      ? `${writingScoreLine}。${evidenceCopy.resultSummary}`
      : evidenceCopy.resultSummary,
    abilityEvidenceLabel: evidenceCopy.abilityEvidenceLabel,
    source: trainingAttemptContext.source,
    reviewFocus: trainingAttemptContext.reviewFocus,
    platformScope,
    questionMetrics,
    sectionSummaries,
  };
}
